"use client"
import { FC, JSX, useEffect, useState } from "react";
import cn from "classnames";
import { Link } from 'next-view-transitions';
import { RotatingLines } from "react-loader-spinner";

import { fetchCMS } from '@/lib/cms';
import { VideoPlayerHandle } from "@/lib/yt";
import { Button } from '@/components/forms/index';
import useMusicStore, { Track } from '@/state/music';

import { Card } from '@/components/ui/Card';
import { SvgIcon } from "@/components/ui/SvgIcon";
import styles from './artists.module.scss';

const STNNDBY_LABEL = typeof navigator !== "undefined" && navigator.userAgent.match(/Mobile/) ? "SELECT" : "PLAY" ;

type ArtistsPageData = {
  header: string;
  description: string;
  subheader: string;
  submissionLink: string;
};

const Artists: FC = () => {
  const [cmsData, setCmsData] = useState<ArtistsPageData | null>(null);

  useEffect(() => {
    if (!cmsData) {
      const fetchData = async () => {
        const { data } = await fetchCMS<ArtistsPageData>('artists-page', { populate: '*', normalize: true });
        if (data) {
          setCmsData(data);
        }
      };
      fetchData();
    }
  }, [cmsData]);

  const { setCurrentTrackId } = useMusicStore.getState();
  const currentTrackId = useMusicStore(state => state.currentTrackId);
  const playerState = useMusicStore(state => state.playerState);
  const playlist = useMusicStore(state => state.playlist);
  const playerRef = useMusicStore(state => state.playerRef);

  if (!cmsData) return <p>Loading...</p>;

  const renderArtistsProps = { currentTrackId, playerState, setCurrentTrackId, playerRef};

  return (
    <>
      <section className={styles.artistsWrapper}>
        <h1>{cmsData.header}</h1>
        <p>{cmsData.description}</p>
        <Link href="/artists/submit">{cmsData.submissionLink}</Link>

        <h2>{cmsData.subheader}</h2>
        <div className={styles.grid}>
          {playlist.map(renderArtist.bind(null, renderArtistsProps))}
        </div>
      </section>
    </>
  );
};

export default Artists;

type RenderArtistProps = {
  currentTrackId: string | null;
  playerState: number;
  setCurrentTrackId: (id: string | null) => void;
  playerRef: VideoPlayerHandle | null;
};

const renderArtist = (
  {
    currentTrackId,
    playerState,
    setCurrentTrackId,
    playerRef,
  }: RenderArtistProps,
  a: Track,
  index: number,
) => {
  const activeTrack = currentTrackId === a.youtube?.vid;
  let buttonLabel: string | JSX.Element = STNNDBY_LABEL;
  switch (playerState) {
    case -1:
      buttonLabel = activeTrack ? "PLAY": STNNDBY_LABEL;
      break;
    case 1:
      buttonLabel = activeTrack ? "PAUSE": STNNDBY_LABEL;
      break;
    case 2:
      buttonLabel = activeTrack ? "PLAY" : STNNDBY_LABEL;
      break;
    case 3:
      if (activeTrack) {
        buttonLabel = (
          <>
            PLAY
            <RotatingLines
              wrapperClass={styles.spinner}
              height="16"
              width="24"
              strokeColor="white"
              strokeWidth="3"
              animationDuration="1s"
            />
          </>
        );
      }
      break;
    default:
      buttonLabel = STNNDBY_LABEL;
  }
  
  const onPlay = () => {
    if (!activeTrack) {
      playerRef?.pause();
      setCurrentTrackId(a.youtube?.vid);
    }
    playerRef?.togglePlay();
  };

  return (
    <Card hero={`https://i.ytimg.com/vi/${a.youtube?.vid}/hqdefault.jpg`} key={`${index}-${a.artist}`} className={cn(styles.card, { activeTrack })}>
      <div>
        <div className={styles.cardHeader}>
          <h3>{a.artist}</h3>
          <a className={styles.socials} href={a.promoLink} target="_blank" rel="noreferrer" title={`more from ${a.artist}`}>
            <SvgIcon iconName="Globe" size={14} />
            Visit
          </a>
        </div>
        <p>{a.description}</p>
      </div>
      <div className={styles.cardActions}>
        <Button onClick={onPlay} variant="primary">{buttonLabel}</Button>
      </div>
    </Card>
  );
};
